import { Component, OnInit, OnDestroy, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GridsterConfig, GridsterItem, GridType, Gridster } from 'angular-gridster2';
import { TimerService } from '../services/timer/timer.service';
import { ApiService } from '../services/api/api.service';
import { AuthGuard } from '../services/authentication/auth.guard';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { DashboardItemConfig } from '../models/dashboard.model';
import { ItAtpData } from '../models/business-data.model';

@Component({
  standalone: true,
  selector: 'app-it',
  templateUrl: './it.component.html',
  styleUrls: ['./it.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    Gridster,
    GridsterItem,
  ],
})
export class ItComponent implements OnInit, OnDestroy {
  public currentTime: Date = new Date();
  public options!: GridsterConfig;
  public dashboard!: Array<DashboardItemConfig>;
  public tableRows: number = 0;
  public AtpData: ItAtpData = {
    CRMQueue: 0, TransportQueue: 0, AwsQueue: '', AwsAlerts: '', externalIP: '', ping: 0,
    BitcoinPrice: '', LitecoinPrice: '', EtheriumPrice: '', CardanoPrice: '', QuantPrice: '',
    AcceptedMails: 0, FailedMails: 0, FailedMailsPercentage: 0
  };
  public LastUpdate: any;
  public version: string  = 'n/a';

  private destroy$ = new Subject<void>();
  private clockInterval: any;
  private scheduledTimeouts: any[] = [];

  constructor(
    public scheduler: TimerService,
    public apiService: ApiService,
    public auth: AuthGuard,
    private cdr: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    private router: Router
  ) {}

  public ngOnInit() {
    if (environment.production) {
      this.apiService.getAppVersionFromNgSw().then(v => this.version = v || 'n/a');
    }

    this.initializeClock();
    this.setupGridster();
    this.setupDashboard();
    this.initDataFetchers();
  }

  public ngOnDestroy() {
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }
    this.scheduledTimeouts.forEach(timeout => clearTimeout(timeout));
    this.scheduledTimeouts = [];

    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeClock() {
    this.clockInterval = setInterval(() => {
      this.currentTime = new Date();
      this.tableRows = document.querySelectorAll('#table td')?.length ?? 0;
      this.cdr.markForCheck();
    }, 1000);
  }

  private setupGridster() {
    this.options = {
      gridType: GridType.Fit,
      draggable: {
        enabled: true,
        ignoreContentClass: 'gridster-item-content',
        ignoreContent: true,
        dragHandleClass: 'drag-handler',
      },
      resizable: {
        enabled: true,
      },
    };
  }

  private setupDashboard() {
    this.dashboard = [
      { cols: 2, rows: 3, y: 0, x: 0, hasContent: true, contentType: 'logo' },
      { cols: 2, rows: 4, y: 0, x: 0, hasContent: true, contentType: 'warnings' },
      { cols: 1, rows: 1, y: 0, x: 0, hasContent: true, contentType: 'time' },
      { cols: 1, rows: 1, y: 0, x: 0, hasContent: true, contentType: 'network' },
      { cols: 1, rows: 1, y: 0, x: 0, hasContent: true, contentType: 'bitcoin' },
      { cols: 1, rows: 1, y: 0, x: 0, hasContent: true, contentType: 'etherium' },
      { cols: 1, rows: 1, y: 0, x: 0, hasContent: true, contentType: 'cardano' },
      { cols: 1, rows: 1, y: 4, x: 2, hasContent: true, contentType: 'quant' },
    ];
  }

  private initDataFetchers() {
    this.getLocalIP();
    this.scheduleUpdate(() => this.getCRMQueue(), 900000, 600000);
    this.scheduleUpdate(() => this.getTransportQueue(), 900000, 600000);
    this.scheduleUpdate(() => this.getAwsQueue(), 900000, 600000);
    this.scheduleUpdate(() => this.getAwsAlerts(), 900000, 600000);
    this.scheduleUpdate(() => this.getBitcoinPrice(), 60000, 600000);
    this.scheduleUpdate(() => this.getLitecoinPrice(), 60000, 600000);
    this.scheduleUpdate(() => this.getEtheriumPrice(), 60000, 600000);
    this.scheduleUpdate(() => this.getCardanoPrice(), 60000, 600000);
    this.scheduleUpdate(() => this.getQuantPrice(), 60000, 600000);
  }

  private scheduleUpdate(callback: () => void, successDelay: number, errorDelay: number) {
    callback();
    const retry = () => {
      if (this.destroy$.closed) return;
      this.scheduleUpdate(callback, successDelay, errorDelay);
    };
    const timeoutId = setTimeout(retry, successDelay);
    this.scheduledTimeouts.push(timeoutId);
  }

  public getLocalIP() {
    const timeStart = performance.now();
    this.apiService.getIP().pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        const ping = performance.now() - timeStart;
        this.AtpData.ping = ping;
        this.AtpData.externalIP = result.ip;
        if (!this.destroy$.closed) {
          const timeoutId = setTimeout(() => this.getLocalIP(), 5000);
          this.scheduledTimeouts.push(timeoutId);
        }
      },
      error: () => {
        if (!this.destroy$.closed) {
          const timeoutId = setTimeout(() => this.getLocalIP(), 5000);
          this.scheduledTimeouts.push(timeoutId);
        }
      }
    });
  }

  public getCRMQueue() {
    this.apiService.getValuations(1, 1, '', '', false, '["crmRetail", "externalBidQueue"]')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => this.AtpData.CRMQueue = Number(res.paginator.records)
      });
  }

  public getTransportQueue() {
    this.apiService.getTransports(1, 1, '', '', false, '["queued"]')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => this.AtpData.TransportQueue = Number(res.paginator.records)
      });
  }

  public getAwsQueue() {
    this.apiService.getQueues(100, 1, '', '', false, '["dlq"]')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => this.AtpData.AwsQueue = res.data
      });
  }

  public getAwsAlerts() {
    this.apiService.getAwsAlerts(100, 1, '', '', false, '["activated"]')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => this.AtpData.AwsAlerts = res.data
      });
  }

  public getBitcoinPrice() {
    this.apiService.getBitcoinPrice()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => this.AtpData.BitcoinPrice = res
      });
  }

  public getLitecoinPrice() {
    this.apiService.getLitecoinPrice()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => this.AtpData.LitecoinPrice = String(res)
      });
  }

  public getEtheriumPrice() {
    this.apiService.getEtheriumPrice()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => this.AtpData.EtheriumPrice = res
      });
  }

  public getCardanoPrice() {
    this.apiService.getCardanoPrice()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => this.AtpData.CardanoPrice = res
      });
  }

  public getQuantPrice() {
    this.apiService.getQuantPrice()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => this.AtpData.QuantPrice = res
      });
  }

  public logout(): void {
    this.authenticationService.logout();
  }
}
